import api from "./axios";
import { AttendanceStatusResponse } from "../types/attendance";

export const getTodayAttendance = async (): Promise<AttendanceStatusResponse> => {
    const response = await api.get("/attendance/my/today");
    return response.data;
};

export const checkIn = async (formData: FormData): Promise<any> => {
    const response = await api.post("/attendance/check-in", formData);
    return response.data;
};

export const checkOut = async (formData: FormData): Promise<any> => {
    const response = await api.post("/attendance/check-out", formData);
    return response.data;
};

// ── Manual Mark date rules ───────────────────────────────────────────────────
// Mirrors server/src/helpers/attendance_settings.helper.ts: a future date is
// never markable, and today only while Masters > Manual Attendance allows it.
// "Today" is the IST calendar day, the same day the API uses.

export interface ManualMarkSettings {
    allow_today: boolean;
    today: string;
    max_date: string;
    updated_at: string | null;
    updated_by: { id: number; first_name: string; last_name: string } | null;
}

export const getTodayInIST = (): string =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const previousDay = (date: string): string => {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 1);
    return d.toISOString().slice(0, 10);
};

/** The latest YYYY-MM-DD Manual Mark accepts. */
export const getManualMarkMaxDate = (allowToday: boolean): string => {
    const today = getTodayInIST();
    return allowToday ? today : previousDay(today);
};

/** Why `date` cannot be manually marked, or null when it can. */
export const getManualMarkDateError = (date: string, allowToday: boolean): string | null => {
    const today = getTodayInIST();
    if (!date) return "Please select the attendance date";
    if (date > today) return "Attendance cannot be marked manually for a future date";
    if (date === today && !allowToday) return "Manual attendance for today's date is currently disabled";
    return null;
};

export const getManualMarkSettings = async (): Promise<ManualMarkSettings> => {
    const response = await api.get("/attendance/manual-mark/settings");
    return response.data.data;
};

export const updateManualMarkSettings = async (allowToday: boolean): Promise<ManualMarkSettings> => {
    const response = await api.put("/attendance/manual-mark/settings", { allow_today: allowToday });
    return response.data.data;
};

// ── Leave Applied ──────────────────────────────────────────────────────────
// A day whose work status is "leave" is shown as "Leave Applied" throughout
// Attendance. It carries a leave type (attendances.leave_type, a LeaveType
// value on the server); a Leave day marked without one defaults to Casual.

export const ATTENDANCE_LEAVE_TYPE_OPTIONS = [
    { value: "casual", label: "Casual" },
    { value: "medical", label: "Medical" },
    { value: "accidental", label: "Accidental" },
    // Stored as "other"; shown everywhere as Special Approval.
    { value: "other", label: "Special Approval" },
];

export const DEFAULT_ATTENDANCE_LEAVE_TYPE = "casual";

// The retired "adjusted" type reads as Special Approval, as it does on the server.
export const getAttendanceLeaveTypeLabel = (leaveType?: string | null): string => {
    if (!leaveType) return "";
    return (
        ATTENDANCE_LEAVE_TYPE_OPTIONS.find((option) => option.value === leaveType)?.label ??
        "Special Approval"
    );
};

/**
 * HR status as Attendance displays it. The server still stores an approved
 * Leave day as hr_status "absent"; it reads "leave_applied" here so it is never
 * shown or counted as Absent. A Leave day still awaiting approval stays
 * "pending" — only an approved leave counts as Leave Applied.
 */
export const getDisplayHRStatus = (hrStatus?: string | null, workStatus?: string | null): string => {
    const hr = String(hrStatus ?? "").toLowerCase().trim();
    if (hr === "absent" && String(workStatus ?? "").toLowerCase().trim() === "leave") return "leave_applied";
    return hr;
};

export const getDisplayHRStatusLabel = (hrStatus?: string | null, workStatus?: string | null): string => {
    switch (getDisplayHRStatus(hrStatus, workStatus)) {
        case "present": return "Present";
        case "absent": return "Absent";
        case "leave_applied": return "Leave Applied";
        case "pending": return "Pending Approval";
        case "not_marked": return "Not Marked";
        default: return hrStatus ? String(hrStatus) : "N/A";
    }
};

/** Badge colours for a displayed HR status (Leave Applied is blue). */
export const getDisplayHRStatusBadgeClasses = (hrStatus?: string | null, workStatus?: string | null): string => {
    switch (getDisplayHRStatus(hrStatus, workStatus)) {
        case "present": return "bg-green-100 text-green-700";
        case "leave_applied": return "bg-blue-100 text-blue-700";
        case "pending": return "bg-gray-100 text-gray-700";
        default: return "bg-red-100 text-red-700";
    }
};
